import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { Spinner } from '../components/Spinner';
import {
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const FEATURES = [
  'Automated and efficient lead processing',
  'Real-time and intelligent routing',
  'Enhanced customer communication',
  'Simple, fast and easy to use',
];

export const Login = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    await login(data);
    setIsSubmitting(false);
  };

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2" style={{ minHeight: 560 }}>

        {/* ── Left: branding panel (dark + orange) ── */}
        <div className="relative hidden md:flex flex-col justify-between p-10 text-white overflow-hidden bg-gradient-to-br from-gray-900 via-gray-900 to-[#8c3912]">
          {/* decorative road swoosh */}
          <svg className="absolute -bottom-10 -left-6 w-[120%] opacity-20" viewBox="0 0 400 120" fill="none">
            <path d="M0 90 Q200 40 400 80" stroke="#f97e2e" strokeWidth="14" strokeLinecap="round" />
            <path d="M0 104 Q200 56 400 96" stroke="#ffffff" strokeWidth="3" strokeDasharray="6 12" strokeLinecap="round" />
          </svg>

          <div className="relative">
            <div className="text-2xl font-extrabold tracking-tight">
              Drive <span className="text-accent-400">on Wheels</span>
            </div>
            <p className="mt-1 text-sm text-gray-400">Lead Management System</p>
          </div>

          <div className="relative">
            <h2 className="text-3xl font-bold leading-snug mb-6">
              One stop solution<br />to manage your leads
            </h2>
            <ul className="space-y-3">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm text-gray-200">
                  <CheckCircleIcon className="h-5 w-5 text-accent-400 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <p className="relative text-xs text-gray-500">© {new Date().getFullYear()} Drive on Wheels</p>
        </div>

        {/* ── Right: login form ── */}
        <div className="flex flex-col justify-center px-8 sm:px-12 py-12">
          {/* Logo (transparent PNG, natural aspect ratio) */}
          <img src="/logo-dow.png" alt="Drive on Wheels" className="mx-auto mb-8 w-52 h-auto object-contain" />

          <h1 className="text-xl font-bold text-gray-900 text-center">Welcome back</h1>
          <p className="text-sm text-gray-500 text-center mt-1 mb-7">Sign in to your account to continue</p>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Enter a valid email address',
                  },
                })}
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                className="input"
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  {...register('password', { required: 'Password is required' })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••••••"
                  className="input pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || loading}
              className="btn-primary w-full py-3 text-sm tracking-widest uppercase font-bold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting || loading
                ? <span className="flex items-center justify-center gap-2"><Spinner size="sm" />Signing in...</span>
                : 'Login'}
            </button>
          </form>

          <p className="mt-7 text-sm text-gray-500 text-center">
            Trouble logging in? Contact admin at{' '}
            <a href="mailto:saleshead@driveonwheels.com" className="text-primary-600 hover:underline">saleshead@driveonwheels.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};
